import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import { useDismiss } from '@/hooks/useDismiss'
import type { User } from '@/types'

interface UserActionsMenuProps {
  user: User
  busy: boolean
  /** The signed-in admin's own row: the server refuses to delete the calling admin. */
  isSelf: boolean
  onViewDetails: (user: User) => void
  onResetPassword: (user: User) => void
  onToggleActive: (user: User) => void
  onDelete: (user: User) => void
}

/** The "⋯" menu on a Users row. Closes on any outside click, Escape, or after a choice. */
export function UserActionsMenu({
  user,
  busy,
  isSelf,
  onViewDetails,
  onResetPassword,
  onToggleActive,
  onDelete,
}: UserActionsMenuProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  const choose = (action: (user: User) => void) => () => {
    setOpen(false)
    action(user)
  }

  const itemClass = 'block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t('users.actions.menu')}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-10 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          <button role="menuitem" onClick={choose(onViewDetails)} className={itemClass}>
            {t('users.actions.viewDetails')}
          </button>
          <button role="menuitem" onClick={choose(onResetPassword)} disabled={busy} className={itemClass}>
            {t('users.actions.resetPassword')}
          </button>
          <button role="menuitem" onClick={choose(onToggleActive)} disabled={busy} className={itemClass}>
            {user.isActive ? t('users.actions.disable') : t('users.actions.enable')}
          </button>
          {!isSelf && (
            <button
              role="menuitem"
              onClick={choose(onDelete)}
              className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            >
              {t('users.actions.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
